import type { Dispatch, SetStateAction } from 'react';
import { Modal } from '../../../components/common/Modal';
import type { PendingSubmitData } from '../hooks/useWorkOrderForm';
import { useLanguage } from '../../../features/language/useLanguage';

export const WorkOrderModals = ({
  isMismatchModalOpen,
  setIsMismatchModalOpen,
  pendingSubmitData,
  setPendingSubmitData,
  isOutputGreaterModalOpen,
  setIsOutputGreaterModalOpen,
  warningMessage,
  setWarningMessage,
}: {
  isMismatchModalOpen: boolean;
  setIsMismatchModalOpen: (value: boolean) => void;
  pendingSubmitData: PendingSubmitData | null;
  setPendingSubmitData: Dispatch<SetStateAction<PendingSubmitData | null>>;
  isOutputGreaterModalOpen: boolean;
  setIsOutputGreaterModalOpen: (value: boolean) => void;
  warningMessage: string | null;
  setWarningMessage: (value: string | null) => void;
}) => {
  const { t } = useLanguage();
  return (
    <>
      <Modal
        isOpen={isMismatchModalOpen}
        onClose={() => {
          setIsMismatchModalOpen(false);
          setPendingSubmitData(null);
        }}
        title={t('workOrders.modals.totalUsedWeightMismatch')}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{t('workOrders.modals.totalUsedWeightMessage')}</p>
          {pendingSubmitData && (
            <div className="text-sm text-gray-700">
              <div>
                {t('workOrders.table.roastInput')}: <span className="font-mono">{pendingSubmitData.inputWeight.toFixed(3)} kg</span>
              </div>
              <div>
                {t('workOrders.modals.totalUsedWeight')}:{' '}
                <span className="font-mono">{pendingSubmitData.totalUsedFromLots.toFixed(3)} kg</span>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500">{t('workOrders.modals.adjustInputOrLots')}</p>
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => {
                setIsMismatchModalOpen(false);
                setPendingSubmitData(null);
              }}
            >
              {t('workOrders.modals.back')}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isOutputGreaterModalOpen}
        onClose={() => {
          setIsOutputGreaterModalOpen(false);
        }}
        title={t('workOrders.modals.roastOutputExceedsInput')}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{t('workOrders.modals.outputCannotExceedInput')}</p>
          {pendingSubmitData && (
            <div className="text-sm text-gray-700">
              <div>
                {t('workOrders.table.roastInput')}: <span className="font-mono">{pendingSubmitData.inputWeight.toFixed(3)} kg</span>
              </div>
              <div>
                {t('workOrders.table.roastOutput')}: <span className="font-mono">{pendingSubmitData.outputWeight.toFixed(3)} kg</span>
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500">{t('workOrders.modals.adjustOutput')}</p>
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => {
                setIsOutputGreaterModalOpen(false);
              }}
            >
              {t('workOrders.modals.back')}
            </button>
          </div>
        </div>
      </Modal>
      <Modal isOpen={!!warningMessage} onClose={() => setWarningMessage(null)} title={t('workOrders.modals.warning')}>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">{warningMessage}</p>
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              onClick={() => setWarningMessage(null)}
            >
              {t('workOrders.modals.ok')}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
